import React, { useState, useRef } from 'react';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DateSelector14Days = ({ onDatesChange }) => {
  const { t, i18n } = useTranslation();
  const [selectedDates, setSelectedDates] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const touchTimeoutRef = useRef(null);
  const longPressActiveRef = useRef(false);

  // Generate next 14 days
  const generateDays = () => {
    const days = [];
    const today = new Date();
    // Normalize locale to prevent issues with tags like "en-US@posix"
    let locale = i18n.language || 'en';
    // Remove invalid parts from locale tag
    locale = locale.split('@')[0].split('.')[0];
    // Fallback to 'en' if locale is still invalid
    if (!locale || locale.length < 2) {
      locale = 'en';
    }
    
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      try {
        days.push({
          date: date,
          dateString: date.toISOString().split('T')[0],
          dayName: date.toLocaleDateString(locale, { weekday: 'short' }),
          dayNumber: date.getDate(),
          monthName: date.toLocaleDateString(locale, { month: 'short' }),
          isToday: i === 0
        });
      } catch (error) {
        // Fallback to 'en' if locale is invalid
        days.push({
          date: date,
          dateString: date.toISOString().split('T')[0],
          dayName: date.toLocaleDateString('en', { weekday: 'short' }),
          dayNumber: date.getDate(),
          monthName: date.toLocaleDateString('en', { month: 'short' }),
          isToday: i === 0
        });
      }
    }
    return days;
  };

  const days = generateDays();

  const toggleDate = (dateString) => {
    setSelectedDates(prev => {
      if (prev.includes(dateString)) {
        const newDates = prev.filter(d => d !== dateString);
        onDatesChange?.(newDates);
        return newDates;
      } else {
        const newDates = [...prev, dateString].sort();
        onDatesChange?.(newDates);
        return newDates;
      }
    });
  };

  const selectRange = (startDateString, endDateString) => {
    const startIdx = days.findIndex(d => d.dateString === startDateString);
    const endIdx = days.findIndex(d => d.dateString === endDateString);
    
    if (startIdx === -1 || endIdx === -1) return;
    
    const [minIdx, maxIdx] = [Math.min(startIdx, endIdx), Math.max(startIdx, endIdx)];
    const rangeDates = days.slice(minIdx, maxIdx + 1).map(d => d.dateString);
    
    setSelectedDates(rangeDates);
    onDatesChange?.(rangeDates);
  };

  // Desktop: Mouse events
  const handleMouseDown = (dateString) => {
    setIsSelecting(true);
    setStartDate(dateString);
    toggleDate(dateString);
  };

  const handleMouseEnter = (dateString) => {
    if (isSelecting && startDate) {
      selectRange(startDate, dateString);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    setStartDate(null);
  };

  // Mobile: Touch events
  const handleTouchStart = (dateString) => {
    longPressActiveRef.current = false;
    
    // Long press detection
    touchTimeoutRef.current = setTimeout(() => {
      longPressActiveRef.current = true;
      setIsSelecting(true);
      setStartDate(dateString);
      
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  };

  const handleTouchMove = (e) => {
    if (longPressActiveRef.current && isSelecting) {
      e.preventDefault();
      const touch = e.touches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const dateString = element?.getAttribute('data-date');
      
      if (dateString && startDate) {
        selectRange(startDate, dateString);
      }
    }
  };

  const handleTouchEnd = (dateString) => {
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }
    
    if (!longPressActiveRef.current) {
      // Quick tap - toggle single date
      toggleDate(dateString);
    }
    
    setIsSelecting(false);
    setStartDate(null);
    longPressActiveRef.current = false;
  };

  const clearSelection = () => {
    setSelectedDates([]);
    onDatesChange?.([]);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900">{t('calendar.title')}</h3>
        </div>
        {selectedDates.length > 0 && (
          <button
            onClick={clearSelection}
            className="text-sm text-slate-600 hover:text-slate-900 font-medium"
          >
            {t('calendar.clear')} ({selectedDates.length})
          </button>
        )}
      </div>

      <div className="text-xs text-slate-500 mb-3">
        {t('calendar.desktopInstructions')} • {t('calendar.mobileInstructions')}
      </div>

      <div 
        className="grid grid-cols-7 gap-1 sm:gap-2 select-none"
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchEnd={() => {
          setIsSelecting(false);
          setStartDate(null);
          longPressActiveRef.current = false;
        }}
      >
        {days.map((day) => {
          const isSelected = selectedDates.includes(day.dateString);
          const isStart = selectedDates[0] === day.dateString;
          const isEnd = selectedDates[selectedDates.length - 1] === day.dateString;

          return (
            <div
              key={day.dateString}
              data-date={day.dateString}
              className={`
                relative cursor-pointer rounded-xl p-2 sm:p-3 transition-all duration-200
                ${
                  isSelected
                    ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30 scale-105'
                    : day.isToday
                    ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 hover:border-orange-400'
                    : 'bg-white border border-slate-200 hover:border-blue-400 hover:shadow-md'
                }
                ${
                  isStart ? 'ring-2 ring-blue-400 ring-offset-2' : ''
                }
                hover:scale-105 active:scale-95
              `}
              onMouseDown={() => handleMouseDown(day.dateString)}
              onMouseEnter={() => handleMouseEnter(day.dateString)}
              onTouchStart={() => handleTouchStart(day.dateString)}
              onTouchMove={handleTouchMove}
              onTouchEnd={() => handleTouchEnd(day.dateString)}
              data-testid={`date-cell-${day.dateString}`}
            >
              {day.isToday && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              )}
              
              <div className="text-center">
                <div className={`text-[10px] sm:text-xs font-semibold mb-0.5 sm:mb-1 ${
                  isSelected ? 'text-blue-100' : 'text-slate-500'
                }`}>
                  {day.dayName}
                </div>
                <div className={`text-xl sm:text-2xl font-black mb-0.5 ${
                  isSelected ? 'text-white' : day.isToday ? 'text-orange-600' : 'text-slate-900'
                }`}>
                  {day.dayNumber}
                </div>
                <div className={`text-[10px] sm:text-xs font-medium ${
                  isSelected ? 'text-blue-200' : 'text-slate-400'
                }`}>
                  {day.monthName}
                </div>
              </div>

              {isSelected && (
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent rounded-xl pointer-events-none"></div>
              )}
            </div>
          );
        })}
      </div>

      {selectedDates.length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm font-semibold text-slate-900 mb-2">
            {t('calendar.selected')}: {selectedDates.length} {selectedDates.length === 1 ? t('calendar.day') : t('calendar.days')}
          </p>
          <p className="text-xs text-slate-600">
            {selectedDates[0]} {selectedDates.length > 1 && `→ ${selectedDates[selectedDates.length - 1]}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default DateSelector14Days;
